import { createInstance, type Types } from '@amplitude/analytics-browser'
import type { Router } from 'vue-router'
import featuresStore from '@/modules/features/features.store'
import { FeatureFlags } from '@/modules/features/feature-flags'
import { B2bActivityEvent } from './b2b-activity-event.enum'
import type { B2bActivityProperties } from './b2b-activity-properties.type'

const PAGE_VIEWED = '[Amplitude] Page Viewed'
const PAGE_PATH = '[Amplitude] Page Path'
// The SDK names its unsent queue AMP_unsent_<key10> whatever the instance, so
// on the shared nonprod origin it would send the applicant queue as its own.
const UNSENT_KEY_PREFIX = 'B2B_'
const MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1000

const ACTIVITY_EVENTS = new Set<string>(Object.values(B2bActivityEvent))

let launched: Promise<Types.BrowserClient | undefined> | undefined
let client: Types.BrowserClient | undefined
type ActivityProperties = Record<string, Types.ValidPropertyType>

let unsentActivities: [B2bActivityEvent, ActivityProperties][] = []
let sharedProperties: Record<string, Types.ValidPropertyType> = {}
let held: Promise<void> | undefined
let endHold = () => {}

const unsentStorage: Types.Storage<Types.Event[]> = {
  isEnabled: async () => {
    try {
      localStorage.setItem(UNSENT_KEY_PREFIX, '')
      localStorage.removeItem(UNSENT_KEY_PREFIX)
      return true
    } catch {
      return false
    }
  },
  get: async (key) => {
    try {
      const events: Types.Event[] = JSON.parse(
        localStorage.getItem(UNSENT_KEY_PREFIX + key) ?? '[]',
      )
      return events.filter(
        (event) => !event.time || Date.now() - event.time < MAX_QUEUE_AGE_MS,
      )
    } catch {
      return undefined
    }
  },
  getRaw: async (key) =>
    localStorage.getItem(UNSENT_KEY_PREFIX + key) ?? undefined,
  set: async (key, events) => {
    try {
      localStorage.setItem(UNSENT_KEY_PREFIX + key, JSON.stringify(events))
    } catch {
      // The SDK calls set without awaiting it, so a full storage would reject unhandled.
    }
  },
  remove: async (key) => localStorage.removeItem(UNSENT_KEY_PREFIX + key),
  reset: async () => {},
}

const options = (userId: string): Types.BrowserOptions => ({
  instanceName: 'b2b',
  userId,
  serverZone: 'EU',
  identityStorage: 'localStorage',
  storageProvider: unsentStorage,
  flushMaxRetries: 5,
  remoteConfig: { fetchRemoteConfig: false },
  trackingOptions: { ipAddress: false },
  autocapture: {
    attribution: false,
    fileDownloads: false,
    formInteractions: false,
    pageViews: { trackHistoryChanges: 'pathOnly' },
    sessions: true,
    elementInteractions: false,
    frustrationInteractions: false,
    networkTracking: false,
    webVitals: false,
    performanceTracking: false,
    pageUrlEnrichment: false,
  },
})

// Added after init so it runs after the page view plugin's page counter; the
// screen comes from the event's own path, tracked before the route guard runs.
const enrichment = (router: Router): Types.EnrichmentPlugin => ({
  name: 'b2b-portal-properties',
  type: 'enrichment',
  execute: async (event) => {
    while (held) await held
    event.event_properties =
      event.event_type === PAGE_VIEWED
        ? {
            screen: router
              .resolve(String(event.event_properties?.[PAGE_PATH]))
              .name?.toString(),
            ...sharedProperties,
          }
        : ACTIVITY_EVENTS.has(event.event_type)
          ? { ...event.event_properties, ...sharedProperties }
          : { ...sharedProperties }
    return event
  },
})

// The SDK sends session_end for a stored expired session under the stored
// userId, whatever options.userId says; with a shared key that is the applicant's.
const storedUserId = (key: string): unknown => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '{}')?.userId
  } catch {
    return undefined
  }
}

const dropForeignIdentity = (apiKey: string, userId: string) => {
  const key = `AMP_${apiKey.substring(0, 10)}`
  if (storedUserId(key) === userId) return
  try {
    localStorage.removeItem(key)
  } catch {
    return
  }
}

const launch = async (userId: string) => {
  const apiKey = import.meta.env.VITE_AMPLITUDE_B2B_API_KEY
  if (!apiKey) return undefined

  const flags = await featuresStore.freshFlags()
  if (!flags[FeatureFlags.B2bTracking]) return undefined

  // Imported here: the router imports the app store, which imports this service.
  const { default: router } = await import('@/router')
  dropForeignIdentity(apiKey, userId)
  const instance = createInstance()
  await instance.init(apiKey, options(userId)).promise
  await instance.add(enrichment(router)).promise
  return instance
}

const send = (event: B2bActivityEvent, properties: ActivityProperties) => {
  try {
    client?.track(event, properties)
  } catch (error) {
    console.warn('B2B activity could not be tracked', error)
  }
}

const track = <E extends B2bActivityEvent>(
  event: E,
  properties: B2bActivityProperties[E],
) => {
  if (client) send(event, properties)
  else unsentActivities.push([event, properties])
}

const hold = () => {
  held ??= new Promise((resolve) => {
    endHold = resolve
  })
}

const release = () => {
  held = undefined
  endHold()
}

const start = async ({
  amplitudeUserId,
  isInternal,
  companyId,
}: {
  amplitudeUserId?: string
  isInternal?: boolean
  companyId?: number | null
}) => {
  if (!amplitudeUserId || companyId == null) return

  sharedProperties = {
    portal: 'b2b',
    company_id: companyId,
    is_internal: !!isInternal,
  }
  try {
    launched ??= launch(amplitudeUserId)
    client = await launched
  } catch (error) {
    console.warn('B2B tracking could not start', error)
  }
  if (client) {
    unsentActivities.forEach(([event, properties]) => send(event, properties))
    unsentActivities = []
  }
  release()
}

export default { start, hold, release, track }
